<template>
	<div class="filter-release-year">
		<div class="filter-toolbar">
			<span class="title">{{ $t('WEBAPP_RELEASEYEAR') }}</span>
			<ResetButton :filterNames="['release_year_from', 'release_year_until']" />
		</div>
		<div class="jw-padding">
			<RangeInput class="range" v-model="releaseYear" :min="min" :max="max">
				<template #start>{{ min }}</template>
				<template #end>{{ max }}</template>
			</RangeInput>

			<div style="display: flex; flex-flow: wrap">
				<ToggleButton
					class="filter-release-year__button--this-year"
					:active="presetFiltersActive.WEBAPP_RELEASEYEAR_THIS"
					:icon="'jw-check-icon'"
					@click.prevent="setPreset({ lower: max, upper: max, key: 'WEBAPP_RELEASEYEAR_THIS' })"
				>
					{{ $t('WEBAPP_RELEASEYEAR_THIS') }}
				</ToggleButton>

				<ToggleButton
					class="filter-release-year__button--last-year"
					:active="presetFiltersActive.WEBAPP_RELEASEYEAR_LAST"
					:icon="'jw-check-icon'"
					@click.prevent="setPreset({ lower: max - 1, upper: max - 1, key: 'WEBAPP_RELEASEYEAR_LAST' })"
				>
					{{ $t('WEBAPP_RELEASEYEAR_LAST') }}
				</ToggleButton>
			</div>

			<!-- @TODO add dropdown for decade options -->
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';

import RangeInput from '@/ui-components/RangeInput.vue';
import ToggleButton from '@/ui-components/ToggleButton.vue';
import ResetButton from '@/components/buttons/ResetButton.vue';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';

import { CollectionType } from '@/enums/collection-type';
import { isAnyFilterActive } from '@/helpers/filter-helper';

const props = defineProps<{
	collectionType: CollectionType;
	currentFilters: FilterCollection;
}>();

const min = 1900;
const max = new Date().getFullYear();

const router = useRouter();
const filterStore = useFilterStore();

const releaseYear = ref();

const presetFiltersActive = computed(() => ({
	WEBAPP_RELEASEYEAR_THIS:
		props.currentFilters.release_year_from === max &&
		!isAnyFilterActive(['release_year_until'], props.currentFilters),
	WEBAPP_RELEASEYEAR_LAST:
		props.currentFilters.release_year_from === max - 1 && props.currentFilters.release_year_until === max - 1,
}));

const rangeValue = computed<RangeDualKnobValue>(() => ({
	lower: props.currentFilters.release_year_from || min,
	upper: props.currentFilters.release_year_until || max,
}));

function setPreset(value: RangeDualKnobValue & { key: string }) {
	const { release_year_from, release_year_until } = props.currentFilters;

	const lower = release_year_from === value.lower ? null : value.lower;
	const upper = release_year_until === value.upper ? null : value.upper;

	update([lower, upper], value.key);
}

function update(value: any[], key: string) {
	const selectedState = isDateFilterActive(key) ? 0 : 1;

	const newRoute = filterStore.setRange({
		rangeName: 'release_year',
		trackingKey: key,
		upper: value[1] !== max ? value[1] : null,
		lower: value[0] !== min ? value[0] : null,
		selectedState,
	});

	if (newRoute) router.push(newRoute);
}

function isDateFilterActive(key: string): boolean {
	return (
		['WEBAPP_RELEASEYEAR_LAST', 'WEBAPP_RELEASEYEAR_THIS'].includes(key) &&
		(presetFiltersActive.value.WEBAPP_RELEASEYEAR_LAST || presetFiltersActive.value.WEBAPP_RELEASEYEAR_THIS)
	);
}

function setSlideValue(value: RangeDualKnobValue) {
	releaseYear.value = [value.lower, value.upper];
}

watch(rangeValue, (newValue: RangeDualKnobValue) => {
	setSlideValue(newValue);
});

watch(releaseYear, () => {
	if (releaseYear.value[1] === rangeValue.value.upper && releaseYear.value[0] === rangeValue.value.lower) {
		return;
	}

	update(releaseYear.value, 'knob');
});

onMounted(() => {
	setSlideValue(rangeValue.value);
});
</script>

<style lang="scss"></style>
